/* eslint-disable @typescript-eslint/naming-convention */
import { Location } from "@angular/common";
import { Component, OnInit, QueryList, ViewChild, ViewChildren } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatSelectChange } from "@angular/material/select";
import { ActivatedRoute, Router } from "@angular/router";
import { Operation } from "src/app/shared/types/entity.type";
import { allowDeleteEntity, allowUpdateEntity } from "src/app/shared/utils/helpers/permission.helper";
import { AuthenticationService } from "../../../../core/authentication/authentication.service";
import { QueryParams } from "../../../../core/http/query-params.type";
import { LoadingService } from "../../../../core/loader/loading.service";
import { DataTableComponent } from "../../../../shared/components/data-table/data-table.component";
import { DataTable, GetDataEvent } from "../../../../shared/components/data-table/data-table.type";
import { DeleteModalComponent } from "../../../../shared/components/delete-modal/delete-modal.component";
import { DynamicFormComponent } from "../../../../shared/components/dynamic-form/dynamic-form.component";
import { QuestionBase } from "../../../../shared/components/dynamic-form/question-base";
import { DropdownQuestion } from "../../../../shared/components/dynamic-form/question-dropdown";
import { TextboxQuestion } from "../../../../shared/components/dynamic-form/question-textbox";
import { GenericModalComponent } from "../../../../shared/components/generic-modal/generic-modal.component";
import { GenericModalData } from "../../../../shared/components/generic-modal/generic-modal.type";
import { NotificationService } from "../../../../shared/components/notification/notification.service";
import { SearchFilterOption } from "../../../../shared/components/search-bar/search-filter/search-filter.component";
import { ViewAsService } from "../../../../shared/components/view-as/view-as.service";
import { UserInfo } from "../../../../shared/types/user-info";
import { getUpdatedValues } from "../../../../shared/utils/helpers/common.helper";
import { getAssetActions } from "../../assets/asset";
import { AssetTableService } from "../../assets/asset-table.service";
import { AssetService } from "../../assets/asset.service";
import { AssetServiceUI } from "../../assets/asset.service.ui";
import { Asset } from "../../assets/asset.type";
import { CreateAssetService } from "../../assets/create-asset/create-asset.service";
import { CompanyAttachmentTableService } from "../../company-attachments/company-attachment-table.service";
import { CompanyAttachmentService } from "../../company-attachments/company-attachment.service";
import { CompanyAttachment } from "../../company-attachments/company-attachment.type";
import { CreateCompanyAttachmentService } from "../../company-attachments/create-company-attachment/create-company-attachment.service";
import { ContractTableService } from "../../contracts/contract-table.service";
import { ContractService } from "../../contracts/contract.service";
import { Contract } from "../../contracts/contract.type";
import { CreateContractService } from "../../contracts/create-contract/create-contract.service";
import { CreateLocationService } from "../../locations/create-locations/create-locations.service";
import { STATES } from "../../locations/location";
import { LocationTableService } from "../../locations/location-table.service";
import { LocationService } from "../../locations/location.service";
import { Location as LocationEntity } from "../../locations/location.type";
import { PartnerSpecialTypes } from "../../partners/partner.type";
import { UserTableService } from "../../users/user-table.service";
import { UserService } from "../../users/user.service";
import { User } from "../../users/user.type";
import { CompanyService } from "../company.service";
import { Company } from "../company.type";
import { CreateCompanyService } from "../create-company/create-company.service";
import { finalize } from "rxjs/operators";

@Component({
  selector: "app-manage-company",
  templateUrl: "./manage-company.component.html",
  styleUrls: ["./manage-company.component.scss"],
  providers: [
    CreateCompanyService,
    CreateContractService,
    CreateLocationService,
    CreateCompanyAttachmentService,
    CreateAssetService,
  ],
})
export class ManageCompanyComponent implements OnInit {
  @ViewChildren(DataTableComponent) tables: QueryList<DataTableComponent<unknown>>;
  @ViewChild("assetTable") assetTable: DataTableComponent<Asset>;
  @ViewChild(DynamicFormComponent) dynamicForm: DynamicFormComponent;

  currentUser: UserInfo;
  company: Company;
  isEdit: boolean;
  questions: QuestionBase<string | boolean>[];
  operation: Operation;
  companyId: string;
  isEveryTabCreated: boolean;
  salesRepsTableConfig: DataTable<User>;
  contractsTableConfig: DataTable<Contract>;
  locationsTableConfig: DataTable<LocationEntity>;
  assetsTableConfig: DataTable<Asset>;
  usersTableConfig: DataTable<User>;
  companyAttachmentsTableConfig: DataTable<CompanyAttachment>;
  selectedAction: SearchFilterOption;
  assetActions: SearchFilterOption[];
  additionalInformation: string;
  form = new FormGroup({
    assets_page: new FormControl(),
    assets_pageSize: new FormControl(),
  });

  constructor(
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<GenericModalComponent | DeleteModalComponent>,
    public location: Location,
    public contractTableService: ContractTableService,
    public locationTableService: LocationTableService,
    public assetTableService: AssetTableService,
    public userTableService: UserTableService,
    public companyAttachmentTableService: CompanyAttachmentTableService,
    private createContractService: CreateContractService,
    private createLocationService: CreateLocationService,
    private createCompanyAttachmentService: CreateCompanyAttachmentService,
    private router: Router,
    private route: ActivatedRoute,
    private loader: LoadingService,
    private companyService: CompanyService,
    private locationService: LocationService,
    private questionsService: CreateCompanyService,
    private authenticationService: AuthenticationService,
    private notificationService: NotificationService,
    private userService: UserService,
    private assetService: AssetService,
    private contractService: ContractService,
    private companyAttachmentService: CompanyAttachmentService,
    private createAssetService: CreateAssetService,
    private viewAsService: ViewAsService,
    private assetServiceUI: AssetServiceUI,
  ) {
    this.currentUser = this.authenticationService.currentUserInfo;
    this.operation = Operation.update;
    this.loader.show();
  }

  //#region Lifecycle Methods

  ngOnInit(): void {
    this.assetActions = getAssetActions();
    this.companyId = this.route.snapshot.paramMap.get("id");
    this.getCompany();
  }

  //#endregion

  /** Populate form data */
  getCompany(resetForm?: boolean): void {
    this.companyService
      .getCompany(this.companyId, { relations: { partner: true } })
      .subscribe(
        (company: Company) => {
          this.company = company;
          this.buildForm(this.company);
          this.setTabs();
          if (resetForm) {
            this.dynamicForm.resetWithValues(this.questions);
          }
        },
        (error) => {
          this.notificationService.error(error);
          this.router.navigate(["/companies"]);
        },
      )
      .add(() => this.loader.hide());
  }

  /** Populate contracts table */
  getContracts = (event: GetDataEvent<Contract>): void => {
    const { onError, onResult, queryParams } = event;
    this.contractService
      .getContracts({
        ...queryParams,
        relations: { maintenanceProvider: true },
        where: {
          ...queryParams?.where,
          companyId: this.companyId,
        },
      })
      .subscribe(
        (data) => {
          onResult(data);
        },
        (error) => {
          onError(error);
        },
      );
  };

  /** Populate locations table */
  getLocations = (event: GetDataEvent<LocationEntity>): void => {
    const { onError, onResult, queryParams } = event;
    this.locationService
      .getLocations({
        ...queryParams,
        relations: { country: true },
        where: {
          ...queryParams?.where,
          companyId: this.companyId,
        },
      })
      .subscribe(
        (data): void => {
          onResult(data);
        },
        (error): void => {
          onError(error);
        },
      );
  };

  /** Populate assets table */
  getAssets = (event: GetDataEvent<Asset>): void => {
    const { onError, onResult, queryParams } = event;
    this.assetService
      .getAssets({
        ...queryParams,
        relations: {
          location: true,
          internalProvider: true,
          sla: true,
          contract: { maintenanceProvider: true },
          company: { partner: true },
        },
        where: {
          ...queryParams?.where,
          companyId: this.companyId,
        },
      })
      .subscribe(
        (result) => {
          onResult(result);
        },
        (error) => {
          onError(error);
        },
      );
  };

  /** Populate company attachments table */
  getCompanyAttachments = (event: GetDataEvent<CompanyAttachment>): void => {
    const { onError, onResult, queryParams } = event;
    this.companyAttachmentService
      .getCompanyAttachments(this.companyId, {
        ...queryParams,
        relations: { company: true },
      })
      .subscribe(
        (data): void => {
          onResult(data);
        },
        (error): void => {
          onError(error);
        },
      );
  };

  /** Populate users table */
  getUsers = (event: GetDataEvent<User>): void => {
    const { onError, onResult, queryParams } = event;
    this.userService
      .getUsers({
        ...queryParams,
        joinType: "LEFT",
        where: {
          ...queryParams?.where,
          companyId: this.companyId,
        },
      })
      .subscribe(
        (data): void => {
          onResult(data);
        },
        (error): void => {
          onError(error);
        },
      );
  };

  /** Populate sales reps table */
  getSalesReps = (event: GetDataEvent<User>): void => {
    const { onError, onResult, queryParams } = event;
    const where: QueryParams<User>["where"] = {
      ...queryParams?.where,
      isSalesRep: true,
      companies: { id: { $in: [this.companyId] } },
      ...(this.currentUser.isGlobal() && { partnerId: this.company.partnerId }),
    };

    this.userService
      .getUsers({
        ...queryParams,
        where,
      })
      .subscribe(
        (data): void => {
          onResult(data);
        },
        (error): void => {
          onError(error);
        },
      );
  };

  //#region Handlers

  onGoBack = (): void => this.goBack();

  onToggleManageCompany = (): void => this.toggleManageCompany();

  onUpdateCompany = (): void => this.updateCompany();

  onDeleteCompany = (): Promise<void> => this.deleteCompany();

  /** @workaround fix an issue when table columns are not displayed properly in the table */
  onTabChange = (): void => this.resetTabs();

  onSendAction(): void {
    this.sendAction();
  }

  //#endregion

  // #region Validators

  isSendActionEnabled = (): boolean => {
    return !(this.selectedAction && this.additionalInformation && this.assetTable?.getSelectedRows()?.length);
  };

  canUpdateCompany() {
    return allowUpdateEntity(this.company, this.currentUser);
  }

  canUpdateSalesRep() {
    return this.currentUser.isGlobalAdmin() || this.currentUser.isPartnerAdmin();
  }

  // #endregion

  //#region Private Methods

  private setTabs = (): void => {
    if (!this.isEveryTabCreated) {
      this.setContractsTab();
      this.setLocationsTab();
      this.setAssetsTab();
      this.setCompanyAttachmentsTab();
      this.setUsersTab();
      this.setSalesRepTab();
      this.isEveryTabCreated = true;
    }
  };

  private setContractsTab = (): void => {
    this.contractsTableConfig = this.contractTableService.getContractsTableConfig({
      entities: { company: this.company },
      hidden: ["company.name", "company.partner.name"],
    });
    this.contractsTableConfig = {
      ...this.contractsTableConfig,
      toolBar: {
        ...this.contractsTableConfig.toolBar,
        addItem: {
          function: this.addContract,
          hidden: this.currentUser.hasRight(["viewer"]),
        },
      },
    };
  };

  private setLocationsTab = (): void => {
    this.locationsTableConfig = this.locationTableService.getLocationsTableConfig({
      entities: { company: this.company },
      hidden: ["company.name"],
    });
    this.locationsTableConfig = {
      ...this.locationsTableConfig,
      toolBar: {
        ...this.locationsTableConfig.toolBar,
        addItem: {
          function: this.addLocation,
          hidden: this.currentUser.hasRight(["viewer"]),
        },
      },
    };
  };

  private setAssetsTab = (): void => {
    const hiddenProperties = ["company.name", "company.partner.name", "dmr", "monitoring"];
    const relutechProperties = ["quotedSla", "quotedPrice", "quotedTerm"];

    if (!this.company.enableAssetCustomFields) {
      hiddenProperties.push("fairMarketValue", "bookValue");
    }

    this.assetsTableConfig = this.assetTableService.getAssetsTableConfig({
      entities: { company: this.company },
      selection: { hidden: false },
      hidden:
        this.company.partner.specialType !== PartnerSpecialTypes.Relutech
          ? hiddenProperties.concat(relutechProperties)
          : hiddenProperties,
    });
    this.assetsTableConfig = {
      ...this.assetsTableConfig,
      toolBar: {
        ...this.assetsTableConfig.toolBar,
        addItem: {
          hidden: this.currentUser.hasRight(["viewer"]),
          function: this.addAsset,
        },
      },
    };
  };

  private setCompanyAttachmentsTab = (): void => {
    this.companyAttachmentsTableConfig = this.companyAttachmentTableService.getCompanyAttachmentsTableConfig();
    this.companyAttachmentsTableConfig = {
      ...this.companyAttachmentsTableConfig,
      toolBar: {
        ...this.companyAttachmentsTableConfig.toolBar,
        addItem: {
          function: this.addAttachment,
          hidden: this.currentUser.hasRight(["viewer"]), // Company type omitted as it is not possible to access into this view
        },
      },
    };
  };

  private setUsersTab = (): void => {
    this.usersTableConfig = this.userTableService.getUsersTableConfig({
      entities: { company: this.company },
      hidden: ["company.name", "partner"],
    });
  };

  private setSalesRepTab = (): void => {
    this.salesRepsTableConfig = this.userTableService.getUsersTableConfig({
      entities: { company: this.company },
      actionColumn: { delete: { hidden: true } },
      hidden: ["partner", "company.name", "type", "right", "deleted"],
    });

    this.salesRepsTableConfig = {
      ...this.salesRepsTableConfig,
      actionColumn: [
        ...this.salesRepsTableConfig.actionColumn,
        {
          icon: "link_off",
          text: "Unlink",
          function: this.unlinkSalesRep,
          hidden: (user) => !allowDeleteEntity(user, this.currentUser),
        },
      ],
      toolBar: {
        ...this.salesRepsTableConfig.toolBar,
        addItem: {
          function: this.linkSalesReps,
        },
      },
    };
  };

  private goBack = (): void => this.location.back();

  private toggleManageCompany = (): void => {
    this.isEdit = !this.isEdit;
  };

  private updateCompany = (): void => {
    this.loader.show();
    const body: any = getUpdatedValues(this.dynamicForm.form, this.company);
    if (body.logo) {
      body.logo = body.logo.split(",")[1];
    }
    this.companyService
      .updateCompany(this.company.id, body)
      .pipe(finalize(() => this.dynamicForm.enable()))
      .subscribe(
        async (company: Company) => {
          const enableAssetCustomFieldsHasChange =
            this.company.enableAssetCustomFields !== company.enableAssetCustomFields;
          this.company = { ...this.company, ...company };
          this.toggleManageCompany();
          this.notificationService.success("Company updated");
          this.buildForm(this.company);
          this.dynamicForm.markQuestionAsPristine("logo");
          if (body.logo && this.viewAsService.getViewAs()?.company) {
            await this.viewAsService.saveInLocalStorage({ companyId: this.company.id });
          }
          if (enableAssetCustomFieldsHasChange) {
            window.location.reload();
          }
        },
        (error) => this.notificationService.error(error || "Could not update company"),
      )
      .add(() => this.loader.hide());
  };

  private deleteCompany = async (): Promise<void> => {
    if (!this.currentUser.isGlobalAdmin()) {
      this.notificationService.error(
        "You do not have access to delete a company. For assistance, please contact portalsupport@nasupport.com",
      );
      return;
    }

    const dialogRef = this.dialog.open(DeleteModalComponent, {
      width: "300px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        name: "company",
        data: this.company,
        url: this.companyService.basePath,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.router.navigate(["/companies"]);
      }
    });
  };

  private cancelCompanyLogo = (): void => {
    this.buildForm(this.company);
    this.dynamicForm.form.controls["logo"].reset();
  };

  private deleteCompanyLogo = (): void => {
    const dialogRef = this.dialog.open(DeleteModalComponent, {
      width: "300px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        name: "company logo",
        data: this.company,
        overwrittenUrl: `${this.companyService.basePath}/${this.company.id}/logo`,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.notificationService.success("Company logo has been deleted");
        this.getCompany(true);
      }
    });
  };

  private buildForm = (company?: Company): void => {
    const options = {
      company,
      operation: this.operation,
      ...(!this.currentUser.hasRight(["viewer"]) && {
        deleteCompanyLogoModal: this.company.logo ? this.deleteCompanyLogo : this.cancelCompanyLogo,
      }),
      ...(this.currentUser.isGlobal() && {
        partnerSkip: false,
        partnerEdit: true,
        canManageClientPriceEdit: true,
        skipManageClientPrice: false,
      }),
      ...(this.currentUser.isPartner() && {
        partnerValue: this.currentUser.partnerId,
        partnerSkip: true,
        partnerEdit: false,
        canManageClientPriceEdit: false,
        skipManageClientPrice: !company.canManageClientPrice,
      }),
      ...(this.currentUser.isGlobalAdmin() && {
        showFairMarketValueAndBookValue: true,
      }),
    };

    this.questionsService.generateQuestions(options).subscribe((qs) => {
      this.questions = this.currentUser.isPartner() ? qs.filter((q) => q.key !== "partnerId") : qs;
    });
  };

  private addContract = (table: DataTableComponent<Contract>): void => {
    const questions: QuestionBase<string | boolean>[] = [];
    this.createContractService
      .generateQuestions({
        partnerSkip: true,
        companyEdit: false,
        companyValue: this.company.id,
        companyOptions: [{ value: this.company.id, label: this.company.name }],
        currentUser: this.currentUser,
        hideReseller: this.currentUser.isCompany() || !this.company.partner?.isResellerNeeded,
      })
      .subscribe((qs) => Object.assign(questions, qs));

    this.dialogRef = this.dialog.open<GenericModalComponent, GenericModalData>(GenericModalComponent, {
      width: "300px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        title: "Add Contract",
        component: {
          clazz: DynamicFormComponent,
          onSubmit: "onSubmit",
          inputs: {
            questions,
            showButtons: false,
          },
          events: [
            {
              eventName: "formSubmit",
              handler: (_dynamicForm: DynamicFormComponent, values: Contract) => {
                this._addContract(values, table);
              },
            },
            {
              eventName: "hasChanged",
              handler: async (dynamicForm: DynamicFormComponent, event: any) => {
                if (event.key === "endDate" || event.key === "startDate") {
                  if (event.key === "startDate") {
                    dynamicForm.getQuestion("endDate").minDate = event.value;
                  }

                  dynamicForm.replaceOrAppendQuestion(
                    this.createContractService.generateContractStatusQuestion(
                      "status",
                      "Status",
                      8,
                      dynamicForm.form.get("startDate").value,
                      dynamicForm.form.get("endDate").value,
                    ),
                  );
                }
              },
            },
          ],
        },
      },
    });
  };

  private _addContract = (contract: Contract, table: DataTableComponent<Contract>): void => {
    for (const propName in contract) {
      if (contract[propName] === "") {
        delete contract[propName];
      }
    }

    this.contractService.createContract(contract).subscribe(
      () => {
        this.notificationService.success("Contract added");
        this.dialogRef.close();
        table.search();
      },
      (error) => {
        console.error(error);
        this.notificationService.error("Could not add Contract:\n" + error);
      },
    );
  };

  private addLocation = (table: DataTableComponent<LocationEntity>): void => {
    const questions: QuestionBase<string | boolean>[] = [];
    this.createLocationService
      .generateQuestions({
        partnerSkip: true,
        companyEdit: false,
        companyValue: this.company.id,
        companyOptions: [{ value: this.company.id, label: this.company.name }],
        currentUser: this.currentUser,
        isChannelWorksSkip: !this.currentUser.isGlobal(),
      })
      .subscribe((qs) => Object.assign(questions, qs));

    this.dialogRef = this.dialog.open<GenericModalComponent, GenericModalData>(GenericModalComponent, {
      width: "300px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        title: "Add Location",
        component: {
          clazz: DynamicFormComponent,
          onSubmit: "onSubmit",
          inputs: {
            questions,
            showButtons: false,
          },
          events: [
            {
              eventName: "hasChanged",
              handler: (dynamicForm: DynamicFormComponent, event: MatSelectChange) => {
                if (event.source.id === "countryId") {
                  // value = 00000000-0000-4000-A000-000000000184 is United States
                  // TODO: review if we can improve this
                  dynamicForm.replaceOrAppendQuestion(
                    event.source.value === "00000000-0000-4000-A000-000000000184"
                      ? new DropdownQuestion({
                          key: "state",
                          label: "State",
                          options: STATES,
                          required: true,
                          dependsOn: {
                            key: "countryId",
                          },
                          order: 8,
                        })
                      : new TextboxQuestion({
                          key: "state",
                          label: "State/Province",
                          required: true,
                          dependsOn: {
                            key: "countryId",
                          },
                          order: 8,
                        }),
                  );
                }
              },
            },
            {
              eventName: "formSubmit",
              handler: (_dynamicForm: DynamicFormComponent, values: LocationEntity) => {
                this._addLocation(values, table);
              },
            },
          ],
        },
      },
    });
  };

  private _addLocation = (location: LocationEntity, table: DataTableComponent<LocationEntity>): void => {
    for (const propName in location) {
      if (location[propName] === "") {
        delete location[propName];
      }
    }

    this.locationService.createLocation(location).subscribe(
      () => {
        this.notificationService.success("Location added");
        this.dialogRef.close();
        table.search();
      },
      (err) => {
        this.notificationService.error("Could not add Location:\n " + err);
      },
    );
  };

  private addAttachment = (table: DataTableComponent<CompanyAttachment>): void => {
    const questions: QuestionBase<string | boolean>[] = [];
    this.createCompanyAttachmentService
      .generateQuestions({
        isDescriptionEditable: true,
        hideFile: false,
        isNameEditable: true,
      })
      .subscribe((qs) => Object.assign(questions, qs));

    this.dialogRef = this.dialog.open<GenericModalComponent, GenericModalData>(GenericModalComponent, {
      width: "300px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        title: "Add Attachment",
        component: {
          clazz: DynamicFormComponent,
          onSubmit: "onSubmit",
          inputs: {
            questions,
            showButtons: false,
          },
          events: [
            {
              eventName: "formSubmit",
              handler: (_dynamicForm: DynamicFormComponent, values: CompanyAttachment) => {
                this._addAttachment(values, table);
              },
            },
          ],
        },
      },
    });
  };

  private _addAttachment = (attachment: CompanyAttachment, table: DataTableComponent<CompanyAttachment>): void => {
    this.loader.show();
    const extension = attachment.file.name.split(".").pop();
    this.companyService.getS3Url(this.company.id, extension).subscribe(
      (observer) => {
        this.createCompanyAttachmentService.uploadFile(observer.url, attachment.file).subscribe(
          () => {
            this.companyService
              .createAttachment({
                name: attachment.name,
                description: attachment.description,
                companyId: this.company.id,
                url: observer.name,
              })
              .subscribe(
                () => {
                  this.loader.hide();
                  this.notificationService.success("Attachment added");
                  this.dialogRef.close();
                  table.search();
                },
                (error) => {
                  this.loader.hide();
                  this.notificationService.error("Could not create attachment:\n" + error);
                },
              );
          },
          () => {
            this.loader.hide();
            this.notificationService.error("Could not create attachment");
          },
        );
      },
      () => {
        this.loader.hide();
        this.notificationService.error("Could not create attachment");
      },
    );
  };

  private resetTabs = (): void => {
    setTimeout(() => this.tables.forEach((table) => table.resetScrollbar()), 0);
  };

  private addAsset = (table: DataTableComponent<Asset>): void => {
    this.assetServiceUI.showAssetCreationModal(
      this.company,
      null,
      this.currentUser,
      table,
      this.createAssetService,
      this.contractService,
      this.assetService,
    );
  };

  private sendAction(): void {
    const assetIds = this.assetTable?.getSelectedRows().reduce((arr, elem) => (arr.push(elem.id), arr), []);
    const data = {
      action: this.selectedAction.value,
      assetIds,
      additionalInformation: this.additionalInformation,
    };

    this.assetService
      .actionRequest(data)
      .subscribe(
        () => {
          this.notificationService.success("Action requested correctly");
          this.assetTable.resetSelectedRows(true);
        },
        (err) => {
          const error = `Action could not be requested:\n ${err}`;
          this.notificationService.error(error);
        },
      )
      .add(() => {
        this.loader.hide();
      });
  }

  private linkSalesReps = async (table: DataTableComponent<User>): Promise<void> => {
    const companyId = this.companyId;

    // Assign users to the partner (used in the popup table)
    const action = (usersTable: DataTableComponent<User>) => {
      if (usersTable.config.selection.selectedRows.length) {
        this.loader.show();
        const body = { users: usersTable.config.selection.selectedRows.map((partner) => partner.id) };
        this.companyService
          .linkUsers(companyId, body)
          .subscribe(
            () => {
              this.notificationService.success("Partner sales representatives linked");
              table.search();
            },
            (error) =>
              this.notificationService.error(`Couldn't link partner sales representatives to company: ${error}`),
          )
          .add(() => this.loader.hide());
      }
    };

    this.dialog.open<GenericModalComponent, GenericModalData<{ config: DataTable<User> }>>(GenericModalComponent, {
      width: "1400px",
      panelClass: "custom-dialog-container",
      data: {
        title: `Link Partner Sales Representatives to ${this.company.name}`,
        buttons: { hide: false, submit: { action } },
        component: {
          clazz: DataTableComponent,
          inputs: {
            config: this.userTableService.getUsersTableConfig({
              id: "users_to_link",
              hidden: ["partner", "company.name", "type", "right", "deleted"],
              actionColumn: { hidden: true },
              inlineEdit: { disabled: () => true },
              syncWithQueryParams: false,
              selection: { hidden: false },
            }),
          },
          events: [
            {
              eventName: "getData",
              handler: (_dynamicForm: any, event: GetDataEvent<User>) => {
                return this.userTableService.getAvailableUsersToLinkToCompany(this.companyId, {
                  ...event,
                });
              },
            },
          ],
        },
      },
    });
  };

  private unlinkSalesRep = (user: User, table: DataTableComponent<User>): void => {
    const dialogRef = this.dialog.open(DeleteModalComponent, {
      width: "300px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        name: "Partner Sales Rep.",
        data: user,
        unlink: true,
        fullUrl: `${this.userService.basePath}/${user.id}/companies`,
        body: { companies: [this.companyId] },
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        table.search({ isDeletingOrUnlinking: true });
      }
    });
  };

  //#endregion
}
